import {
  COMPETITOR_PATH_TOKEN,
  brandIconUrl,
  isActivePipelineFulfillmentStatus,
  relocateAdgepublicAssetUrl,
  TERMINAL_FULFILLMENT_STATUSES,
  type MarketplaceFulfillmentStatus,
} from "@nexus/contracts";
import type { TFunction } from "i18next";

import {
  humanizeConceptLabel,
  humanizePersonaCompetitor,
  humanizePersonaSentence,
  humanizePersonaTitle,
} from "@/features/personas/lib/persona-format";
import type {
  MarketplaceOrder,
  SentScriptsMap,
} from "@/features/marketplace/types/marketplace.types";
import { readCachedCreativeUrlSettings } from "@/lib/queries/system-settings.queries";

import type {
  HubDocumentGroup,
  HubDocumentRow,
  HubOpportunityRow,
} from "../api/persona-opportunity-hub.api";
import type {
  HubChromeOpportunityRow,
  HubChromeOrderRow,
  HubChromeOrderStatus,
  HubChromeScriptGroup,
  HubChromeScriptGroupSentItem,
  HubChromeScriptRow,
} from "../components/page/PersonaOpportunityHubChrome/hub-chrome.types";
import { PERSONA_OPPORTUNITY_DEFAULT_STICK_DAYS } from "../constants/persona-opportunity-tuning-defaults";
import { readPostFlightMetrics } from "./post-flight-opportunity-metrics";
import { buildPersonaOpportunityWhyLine } from "./persona-opportunity-row-model";

/**
 * Hub list DTOs → the rows the hub chrome renders. The chrome is shared with
 * Storybook, so the mapping lives here rather than inside the presentational
 * components.
 */

export function toHubChromeOpportunityRow(
  row: HubOpportunityRow,
  t: TFunction,
): HubChromeOpportunityRow {
  const { kpis, supportingAd } = row;
  const paths = readCachedCreativeUrlSettings();
  const competitorRaw = supportingAd?.competitor?.trim() || null;
  const competitorName = competitorRaw ? humanizePersonaCompetitor(competitorRaw) : null;
  const investmentIndex = kpis.investmentIndex ?? null;
  const angle = humanizePersonaTitle(row.angle ?? row.topicTitle ?? "");
  const isPostFlight = row.setType === "POST_FLIGHT";
  const postFlight = isPostFlight ? readPostFlightMetrics(row.metadata) : null;
  const fromSource = row.feature?.trim() || postFlight?.parentTitle || "";

  return {
    id: row.id,
    personaId: row.personaId ?? "",
    status: row.status,
    angle,
    painPoint: row.painPoint ? humanizePersonaSentence(row.painPoint) : null,
    motivation: row.motivation ? humanizePersonaSentence(row.motivation) : null,
    drivenBy: row.drivenBy,
    why: isPostFlight
      ? (row.topicDescription?.trim() ?? "")
      : buildPersonaOpportunityWhyLine(
      {
        investmentIndex,
        competitorsRunningCount: kpis.competitorsRunningCount,
        competitorsTotal: row.competitorsTotal,
        stickRatePct: kpis.stickRatePct,
        stickDays: row.stickDays ?? PERSONA_OPPORTUNITY_DEFAULT_STICK_DAYS,
        drivenBy: row.drivenBy,
      },
      t,
    ),
    fromLabel: isPostFlight && fromSource ? humanizeConceptLabel(fromSource) : null,
    postFlight,
    creativesCount: kpis.creativesCount,
    competitorsRunningCount: kpis.competitorsRunningCount,
    competitorsTotal: row.competitorsTotal,
    stickRatePct: kpis.stickRatePct,
    duplicatedPct: kpis.duplicatedPct,
    myInvestmentSharePct: kpis.myInvestmentSharePct,
    marketInvestmentSharePct: kpis.marketInvestmentSharePct,
    investmentIndex,
    thumbnailUrl: relocateAdgepublicAssetUrl(supportingAd?.thumbnailUrl, paths),
    mediaUrl: relocateAdgepublicAssetUrl(supportingAd?.url, paths),
    daysLive: supportingAd?.daysLive ?? null,
    duplications: null,
    competitorName,
    competitorInitial: competitorName ? competitorName.charAt(0).toUpperCase() : null,
    competitorProfilePictureUrl: brandIconUrl(paths.competitorsPath, COMPETITOR_PATH_TOKEN, competitorRaw),
    rank: row.rank,
    setId: row.setId,
    setName: row.setName,
    setType: row.setType,
    isCustom: row.isCustom,
    personaName: row.personaTitle ? humanizePersonaTitle(row.personaTitle) : null,
    personaAvatarUrl: row.personaAvatarUrl,
    ...(row.topicTitle ? { topicTitle: row.topicTitle } : {}),
    ...(row.topicDescription ? { topicDescription: row.topicDescription } : {}),
    ...(row.findings ? { findings: row.findings } : {}),
    ...(row.suggestion ? { suggestion: row.suggestion } : {}),
    ...(row.action ? { action: row.action } : {}),
    ...(row.dataset ? { dataset: row.dataset } : {}),
    ...(row.feature ? { feature: row.feature } : {}),
    ...(row.subFeature ? { subFeature: row.subFeature } : {}),
    ...(row.spend != null ? { spendUsd: row.spend } : {}),
    ...(row.benchmarkSpend != null ? { benchmarkSpendUsd: row.benchmarkSpend } : {}),
    ...(row.competitorSpend != null ? { competitorSpendUsd: row.competitorSpend } : {}),
    ...(row.spendPerAngle != null ? { spendPerAngle: row.spendPerAngle } : {}),
    ...(row.benchmarkSpendPerAngle != null
      ? { benchmarkSpendPerAngle: row.benchmarkSpendPerAngle }
      : {}),
    ...(row.competitorSpendPerAngle != null
      ? { competitorSpendPerAngle: row.competitorSpendPerAngle }
      : {}),
    ...(row.kpi != null ? { kpi: row.kpi } : {}),
    ...(row.competitorKpi != null ? { competitorKpi: row.competitorKpi } : {}),
    ...(row.benchmarkKpi != null ? { benchmarkKpi: row.benchmarkKpi } : {}),
    ...(row.engine ? { engine: row.engine } : {}),
    ...(row.industry ? { industry: row.industry } : {}),
    legacyState: row.state,
    ...(row.runDate ? { runDate: row.runDate } : {}),
  };
}

export function toHubChromeScriptRow(
  row: HubDocumentRow,
  sentScripts: SentScriptsMap,
  t: TFunction,
): HubChromeScriptRow {
  const untitled =
    row.kind === "BRIEF"
      ? t("personaOpportunities.hub.scripts.untitledBrief", "Untitled brief")
      : t("personaOpportunities.hub.scripts.untitledScript", "Untitled script");
  const sentMeta = sentScripts[row.id];

  return {
    id: row.id,
    setId: row.setId,
    setName: row.setName,
    opportunityId: row.opportunityId,
    opportunityAngle: row.opportunityLabel ? humanizePersonaTitle(row.opportunityLabel) : "",
    title: row.title?.trim() || untitled,
    kind: row.kind === "BRIEF" ? "brief" : "script",
    // Sends are keyed by script/brief id in the tenant-wide sent map.
    sent: sentMeta != null,
    sentTo: sentMeta
      ? {
          vendorName: sentMeta.vendorName,
          sentAt: sentMeta.sentAt,
          sentByUserName: sentMeta.sentByUserName,
        }
      : null,
    isFavorite: row.isFavorite,
    youtubeFormat: row.youtubeFormat ?? null,
    setType: row.setType,
    slotIndex: row.slotIndex,
    parentScriptId: row.parentScriptId,
    concept: row.concept?.trim() || null,
    broadcastSource: row.broadcastSource ?? null,
    duration: row.duration,
    sceneCount: row.sceneCount,
    hookPackage: row.hookPackage,
    hookCount: row.hookCount ?? null,
    versionCount: row.versionCount,
    createdAt: row.createdAt,
  };
}

export function toHubChromeScriptGroup(
  group: HubDocumentGroup,
  loaded: Pick<
    HubChromeScriptGroup,
    "rows" | "isLoading" | "sentItems" | "isLoadingSentTitles"
  >,
): HubChromeScriptGroup {
  return {
    key: group.opportunityId,
    label: group.opportunityLabel ? humanizePersonaTitle(group.opportunityLabel) : "",
    personaTitle: group.personaTitle ? humanizePersonaTitle(group.personaTitle) : null,
    isCustom: group.isCustom,
    setId: group.setId,
    setName: group.setName,
    scriptCount: group.scriptCount,
    briefCount: group.briefCount,
    ...loaded,
  };
}

/** The tenant-wide sent map, bucketed by the persona opportunity each item belongs to. */
export function sentScriptIdsByOpportunity(
  sentScripts: SentScriptsMap,
): Map<string, { id: string; vendorName: string }[]> {
  const byOpportunity = new Map<string, { id: string; vendorName: string }[]>();
  for (const [id, meta] of Object.entries(sentScripts)) {
    if (!meta.opportunityId) continue;
    const bucket = byOpportunity.get(meta.opportunityId) ?? [];
    bucket.push({ id, vendorName: meta.vendorName });
    byOpportunity.set(meta.opportunityId, bucket);
  }
  return byOpportunity;
}

/**
 * Names a group's sent items from its loaded rows. Once rows have loaded,
 * items missing from them (deleted since sending) are dropped so the badge
 * matches the group's own counts.
 */
export function toHubChromeScriptGroupSentItems(
  sent: readonly { id: string; vendorName: string }[],
  loadedRows: readonly HubChromeScriptRow[] | null,
): HubChromeScriptGroupSentItem[] {
  if (!loadedRows) return sent.map((item) => ({ ...item, title: null }));
  const titles = new Map(loadedRows.map((row) => [row.id, row.title]));
  return sent.flatMap((item) => {
    const title = titles.get(item.id);
    return title == null ? [] : [{ ...item, title }];
  });
}

/** The badge collapses the seven fulfillment states into the hub's three. */
function orderStatus(status: MarketplaceFulfillmentStatus): HubChromeOrderStatus {
  if ((TERMINAL_FULFILLMENT_STATUSES as readonly string[]).includes(status)) return "delivered";
  return isActivePipelineFulfillmentStatus(status) ? "in_production" : "sent";
}

/** One hub row per order line, so a batch send lists each script it carried. */
export function toHubChromeOrderRows(
  order: MarketplaceOrder,
  scriptsById: ReadonlyMap<string, HubChromeScriptRow>,
): HubChromeOrderRow[] {
  if (order.scripts.length === 0) {
    return [
      {
        id: order.id,
        setId: "",
        setName: "",
        opportunityId: "",
        scriptId: "",
        title: order.vendor.name,
        vendor: order.vendor.name,
        status: orderStatus(order.fulfillmentStatus),
      },
    ];
  }

  return order.scripts.map((line) => {
    const scriptId = line.scriptId ?? line.briefId ?? "";
    const script = scriptId ? scriptsById.get(scriptId) : undefined;
    const stub = line.script;

    return {
      id: `${order.id}:${line.id}`,
      setId: script?.setId ?? stub?.opportunity.setId ?? "",
      setName: script?.setName ?? stub?.opportunity.setName ?? "",
      opportunityId: script?.opportunityId ?? stub?.opportunity.id ?? "",
      scriptId,
      title: script?.title ?? stub?.hook ?? stub?.concept ?? order.vendor.name,
      vendor: order.vendor.name,
      status: orderStatus(line.fulfillmentStatus),
    };
  });
}
